import type { FC } from "react";

import { useQuery } from "@tanstack/react-query";
import type { RawParams } from "@uirouter/react";
import { useCurrentStateAndParams } from "@uirouter/react";
import dayjs from "dayjs";

import { fullMonthAndYear, shortMonthAndFullYear } from "@similarweb/pro-logic-components";

import { translate } from "app/filters/i18nFilter";
import type {
    IAPIParams,
    IRankDistributionOpportunitiesData,
    IRankDistributionOverTimeData,
} from "app/pages/keyword-research/website-analysis/pages/seo-overview/types";
import CountryService from "app/services/CountryService";
import type { IChosenItemParams } from "app/components/compare/WebsiteQueryBar/WebsitePageQueryBarUtils";
import { CenteredFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { BasicDurations, default as DurationService } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { DEVICES_TYPES_FOR_API, EDeviceType } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import { MetricsRow, MetricTitle } from "../StyledComponent";

import { RankingDistributionGraph } from "./ranking-distribution-graph/RankingDistributionGraph";
import { RankingDistributionOpportunities } from "./ranking-distribution-opportunities/RankingDistributionOpportunities";
import { StyledBoxSubtitle } from "./RankingDistributionStyledComponent";

const ENDPOINT_RANK_DISTRIBUTION_OVERTIME = `api/WebsiteAnalysis/Overview/RankDistributionOverTime`;

function getLastDate(distributionOverTimeData: IRankDistributionOverTimeData): string {
    if (Object.keys(distributionOverTimeData?.monthlyDistributions || {}).length > 0) {
        return Object.keys(distributionOverTimeData.monthlyDistributions)
            .map((d) => dayjs(d))
            .sort((a, b) => b.diff(a))[0]
            .format(shortMonthAndFullYear);
    }
    return undefined;
}
function getOpportunitiesFromOverTimeData(
    distributionOverTimeData: IRankDistributionOverTimeData,
): IRankDistributionOpportunitiesData {
    const lastDate = getLastDate(distributionOverTimeData);
    return distributionOverTimeData?.monthlyDistributions[lastDate]
        ? {
              country: distributionOverTimeData?.country,
              data: distributionOverTimeData?.monthlyDistributions[lastDate],
          }
        : undefined;
}
function getAPIParams(params: RawParams, duration: string): IAPIParams {
    const { key, country, webSource, pageFilter } = params;
    const durationObject = DurationService.getDurationData(duration);
    const diff = durationObject.raw.to.diff(durationObject.raw.from, "month") ?? 0;
    const { from, to, isWindow } = durationObject.forAPI;
    const getLastThirteenMonths = duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS || diff < 2;
    return {
        to,
        from,
        isWindow,
        country: country as string,
        webSource: webSource as string,
        keys: key as string,
        includeSubDomains: true,
        device: webSource === EDeviceType.MOBILE ? DEVICES_TYPES_FOR_API.MOBILE : (webSource as string),
        pageFilterJson: pageFilter as IChosenItemParams,
        getLastThirteenMonths,
    };
}
//will be used by the graph
const useRankDistributionData = () => {
    const { params } = useCurrentStateAndParams();
    const { duration } = params;
    const isWindow = BasicDurations.LAST_TWENTY_EIGHT_DAYS === duration;
    const finalDuration = isWindow ? BasicDurations.LAST_MONTH : (duration as string);
    const apiParams = getAPIParams(params, finalDuration);
    const { data: distributionOverTimeData, isLoading } = useQuery({
        queryKey: [
            ENDPOINT_RANK_DISTRIBUTION_OVERTIME,
            apiParams.to,
            apiParams.from,
            apiParams.isWindow,
            apiParams.country,
            apiParams.webSource,
            apiParams.keys,
            apiParams.pageFilterJson,
            apiParams.getLastThirteenMonths,
        ],
        queryFn: async () =>
            DefaultFetchService.getInstance().get<IRankDistributionOverTimeData>(
                ENDPOINT_RANK_DISTRIBUTION_OVERTIME,
                apiParams,
            ),
    });
    return {
        distributionOverTimeData: distributionOverTimeData,
        distributionOpportunitiesData: getOpportunitiesFromOverTimeData(distributionOverTimeData),
        isLoading,
        duration: finalDuration,
    };
};

const RankingDistributionSubTitle: FC = () => {
    const { distributionOverTimeData: data, duration } = useRankDistributionData();
    const durationObject = DurationService.getDurationData(duration);
    const subtitleFilters = [
        {
            filter: "lastDate",
            value: durationObject.raw.to.format(fullMonthAndYear),
        },
        {
            filter: "country",
            countryCode: data?.country,
            value: CountryService.getCountryById(data?.country)?.text,
        },
    ];
    return (
        <CenteredFlexRow>
            <StyledBoxSubtitle filters={subtitleFilters} />
        </CenteredFlexRow>
    );
};
export const RankingDistributionSection: FC = () => {
    // get the latest data from the rankDistributionOverTimeData series for the opportunities section
    const { distributionOpportunitiesData, distributionOverTimeData, isLoading } = useRankDistributionData();
    return (
        <MetricsRow
            gap="16px"
            marginBottom="32px"
        >
            <MetricTitle>
                {translate("wa.traffic.sources.search.seo.overview.summary.ranking.distribution.title")}
                <RankingDistributionSubTitle />
            </MetricTitle>
            <RankingDistributionOpportunities
                data={distributionOpportunitiesData}
                isLoading={isLoading}
            />
            <RankingDistributionGraph
                data={distributionOverTimeData}
                isLoading={isLoading}
            />
        </MetricsRow>
    );
};
